/**
 * DimensionCard Component
 *
 * Individual card for each readiness dimension showing score,
 * status indicator, and action button.
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DimensionAction {
  label: string;
  url: string | null;
  inlineAction?: string;
}

interface DimensionData {
  score: number;
  status: 'green' | 'yellow' | 'red';
  metric: string;
  action: DimensionAction;
  [key: string]: unknown;
}

interface DimensionCardProps {
  title: string;
  dimensionKey: string;
  data: DimensionData;
  onInlineAction?: (action: string) => Promise<void>;
  isActionLoading?: boolean;
}

const statusIcons = {
  green: CheckCircle2,
  yellow: AlertTriangle,
  red: XCircle
};

const statusColors = {
  green: 'text-green-500',
  yellow: 'text-yellow-500',
  red: 'text-red-500'
};

const dimensionLabels: Record<string, string> = {
  productsUploaded: 'Products',
  productsCategorized: 'Categorized',
  categoriesResearched: 'Researched',
  productsEnriched: 'Enriched',
  suspiciousValues: 'Q. Issues',
  categoryDemand: 'Demand',
  buyerPersonas: 'Personas',
  perceivedBenefits: 'Benefits',
  embeddingsDone: 'Embeddings',
  questionWheels: 'Q. Wheels',
  aeoGenerated: 'AEO',
  // ⚠️ WITHOUT THIS ENTRY THE CARD FALLS BACK TO THE RAW KEY. `dimensionTitles[key] || key` and
  // `dimensionLabels[key] || key` both degrade to 'attributeValueRanges' on screen — a fallback
  // that renders rather than fails, so nothing looks broken and the label is simply wrong.
  attributeValueRanges: 'Value Ranges'
};

export function DimensionCard({
  title,
  dimensionKey,
  data,
  onInlineAction,
  isActionLoading
}: DimensionCardProps) {
  const navigate = useNavigate();
  const StatusIcon = statusIcons[data.status];

  const handleAction = async () => {
    if (data.action.inlineAction && onInlineAction) {
      await onInlineAction(data.action.inlineAction);
    } else if (data.action.url) {
      navigate(data.action.url);
    }
  };

  const displayTitle = dimensionLabels[dimensionKey] || title;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
        {/* Title */}
        <p className="text-xs text-muted-foreground mb-1 truncate w-full" title={displayTitle}>
          {displayTitle}
        </p>

        {/* Metric */}
        <p className="text-2xl font-bold mb-1">
          {data.metric}
        </p>

        {/* Status Icon */}
        <StatusIcon className={`h-6 w-6 mb-2 ${statusColors[data.status]}`} />

        {/* Action Button */}
        <Button
          size="sm"
          variant="outline"
          onClick={handleAction}
          disabled={isActionLoading}
          className="w-full text-xs"
        >
          {isActionLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            data.action.label
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
